import React, { useEffect, useState } from 'react';
import { AlertCircle, ArrowRight, CheckCircle2, Loader2, X } from 'lucide-react';
import { PAYSTACK_PUBLIC_KEY, isPaystackConfigured } from '../../config/paystack';
import { courseDetails } from '../../data/courses';
import { useAuth } from '../auth/AuthContext';
import { verifyPaymentServerSide } from '../../lib/payments';
import { enrollInCourse } from '../../lib/lms';
import { validatePromo, discountFor, type Promo } from '../../lib/commerce';
import { sendEnrollmentEmail } from '../../lib/notify';
import { lmsHref } from '../../lib/surface';
import { CheckpointRail, Stamp, type StepState } from '../lms/ui';

interface EnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId?: string;
  courseTitle?: string;
  coursePrice?: string;
  priceValue?: number;
  bundle?: { id: string; title: string; price: string; priceValue: number; courseIds: string[] };
}

type Step = 'details' | 'processing' | 'success' | 'error';

const heardOptions = [
  'Google Search', 'Facebook', 'Instagram', 'LinkedIn', 'YouTube',
  'Friend / Referral', 'Alumni', 'Event / Webinar', 'Other',
];

const initialForm = {
  firstName: '', lastName: '', email: '', phone: '',
  country: 'Nigeria', stateCity: '',
  occupation: '', company: '', heardAbout: '',
};

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({
  isOpen,
  onClose,
  courseId,
  courseTitle,
  coursePrice,
  priceValue: priceValueProp,
  bundle,
}) => {
  const [step, setStep] = useState<Step>('details');
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [verifyMsg, setVerifyMsg] = useState<string>('');
  const [promoCode, setPromoCode] = useState('');
  const [promo, setPromo] = useState<Promo | null>(null);
  const [promoMsg, setPromoMsg] = useState('');
  const [guestCheckout, setGuestCheckout] = useState(false);
  const [accessEmailSent, setAccessEmailSent] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const { user } = useAuth();

  const basePriceValue = bundle?.priceValue ?? priceValueProp ?? ((courseId && courseDetails[courseId]?.priceValue) || 0);
  const priceValue = basePriceValue;
  const discount = promo ? discountFor(promo, priceValue) : 0;
  const payableValue = Math.max(priceValue - discount, 0);

  useEffect(() => {
    if (!isOpen) {
      // reset on close
      setTimeout(() => {
        setStep('details');
        setForm(initialForm);
        setErrors({});
        setErrorMsg('');
        setVerifyMsg('');
        setPromoCode('');
        setPromo(null);
        setPromoMsg('');
        setPaymentRef('');
        setVerifying(false);
        setGuestCheckout(false);
        setAccessEmailSent(false);
        setAccessMessage('');
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user || !isOpen) return;
    const [first, ...rest] = user.name.split(' ');
    setForm((f) => ({
      ...f,
      email: f.email || user.email,
      firstName: f.firstName || first || '',
      lastName: f.lastName || rest.join(' '),
      phone: f.phone || user.phone || '',
    }));
  }, [user, isOpen]);

  if (!isOpen) return null;

  const update = (field: keyof typeof form, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!/^[0-9+()\-\s]{7,}$/.test(form.phone)) e.phone = 'Enter a valid phone number';
    if (!form.country.trim()) e.country = 'Country is required';
    if (!form.stateCity.trim()) e.stateCity = 'State / City is required';
    if (!form.occupation.trim()) e.occupation = 'Occupation is required';
    if (!form.heardAbout) e.heardAbout = 'Please select an option';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const applyPromo = async () => {
    setPromoMsg('');
    setPromo(null);
    if (!promoCode.trim()) return;
    const res = await validatePromo(promoCode, courseId, bundle?.id).catch(() => ({ error: 'Could not check code.' }));
    if ('error' in res) {
      setPromoMsg(res.error);
      return;
    }
    setPromo(res.promo);
    const off = discountFor(res.promo, priceValue);
    setPromoMsg(off > 0 ? `Code applied: ₦${off.toLocaleString('en-NG')} off.` : 'Code applied.');
  };

  const startPayment = async () => {
    if (!validate()) return;
    if (!bundle && !courseId) {
      setErrorMsg('Programme details are unavailable. Please return to the programme register and try again.');
      setStep('error');
      return;
    }

    if (priceValue === 0) {
      if (!user) {
        const freeCourseId = courseId || bundle?.courseIds[0];
        window.location.assign(
          lmsHref(`/signup?course=${encodeURIComponent(freeCourseId || '')}&email=${encodeURIComponent(form.email.trim())}`)
        );
        return;
      }
      setStep('processing');
      setVerifying(false);
      try {
        if (bundle) {
          const { enrollInBundle } = await import('../../lib/commerce');
          await enrollInBundle(user.id, {
            id: bundle.id,
            slug: bundle.id,
            title: bundle.title,
            description: '',
            course_ids: bundle.courseIds,
            price_value: 0,
            compare_at_price: 0,
            is_published: true,
          }, 'free-enrollment');
        } else if (courseId) {
          await enrollInCourse(user.id, courseId);
        }
        await sendEnrollmentEmail({
          toName: `${form.firstName} ${form.lastName}`.trim(),
          courseTitle: bundle?.title || courseTitle || courseId || 'LSEM program',
          ref: 'free-enrollment',
          verified: true,
        });
        setGuestCheckout(false);
        setAccessEmailSent(false);
        setAccessMessage('Your free programme is active.');
        setStep('success');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Could not enroll in the free programme.');
        setStep('error');
      }
      return;
    }

    if (!isPaystackConfigured()) {
      setErrorMsg(
        'Payments are not yet configured. Please add your Paystack public key in src/config/paystack.ts to enable checkout.'
      );
      setStep('error');
      return;
    }
    if (!window.PaystackPop) {
      setErrorMsg('Payment gateway failed to load. Please refresh the page and try again.');
      setStep('error');
      return;
    }
    if (!priceValue || priceValue < 100) {
      setErrorMsg('Course pricing is unavailable. Please contact support.');
      setStep('error');
      return;
    }

    setStep('processing');

    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email: form.email.trim(),
      amount: Math.max(payableValue, 100) * 100, // kobo (min ₦100 for Paystack)
      currency: 'NGN',
      firstname: form.firstName.trim(),
      lastname: form.lastName.trim(),
      phone: form.phone.trim(),
      ref: bundle ? `LSEM-BUNDLE-${bundle.id}-${Date.now()}` : `LSEM-${courseId || 'X'}-${Date.now()}`,
      metadata: {
        courseId: bundle ? undefined : courseId,
        bundleId: bundle?.id,
        courseTitle: bundle?.title || courseTitle,
        promoCode: promo?.code || undefined,
        userId: user?.id,
        fullName: `${form.firstName} ${form.lastName}`.trim(),
        phone: form.phone,
        discount,
        country: form.country,
        stateCity: form.stateCity,
        occupation: form.occupation,
        company: form.company,
        heardAbout: form.heardAbout,
        custom_fields: [
          { display_name: 'Course', variable_name: 'course', value: courseTitle || '' },
          { display_name: 'Location', variable_name: 'location', value: `${form.stateCity}, ${form.country}` },
          { display_name: 'Occupation', variable_name: 'occupation', value: form.occupation },
        ],
      },
      callback: (response) => {
        const ref = response.reference;
        setPaymentRef(ref);
        setVerifying(true);
        setStep('processing');
        void (async () => {
          // Server-side verification writes the ledger and entitlements. For a
          // guest it also provisions the student account and emails an LMS link.
          const v = await verifyPaymentServerSide(ref).catch(() => null);
          setGuestCheckout(!user);
          setAccessEmailSent(!!v?.emailSent);
          setAccessMessage(v?.message ?? '');
          if (!v || !v.ok) {
            if (v?.message) setVerifyMsg(`Payment received — access is pending: ${v.message}`);
            else setVerifyMsg('Payment received, but the verification service could not be reached yet. Keep your Paystack reference handy.');
          } else if (!v.emailSent && v.invited) {
            setVerifyMsg(v.message);
          }
          if (user?.email) {
            void sendEnrollmentEmail({
              toName: `${form.firstName} ${form.lastName}`.trim(),
              courseTitle: bundle?.title || courseTitle || courseId || 'LSEM program',
              ref,
              verified: !!v?.ok,
            });
          }
          setStep('success');
        })();
      },
      onClose: () => {
        // user cancelled
        setVerifying(false);
        setStep('details');
      },
    });

    handler.openIframe();
  };

  const formattedPrice =
    bundle?.price ||
    coursePrice ||
    (priceValue ? `₦${priceValue.toLocaleString('en-NG')}` : '');
  const displayTitle = bundle?.title || courseTitle;

  const steps: { label: string; state: StepState }[] =
    step === 'success'
      ? [
          { label: 'Details', state: 'done' },
          { label: 'Payment', state: 'done' },
          { label: 'Access', state: 'done' },
        ]
      : step === 'processing'
        ? [
            { label: 'Details', state: 'done' },
            { label: 'Payment', state: 'current' },
            { label: 'Access', state: 'todo' },
          ]
        : [
            { label: 'Details', state: step === 'details' ? 'current' : 'done' },
            { label: 'Payment', state: step === 'error' ? 'current' : 'todo' },
            { label: 'Access', state: 'todo' },
          ];

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-start justify-center px-3 py-6 sm:px-6 sm:py-10">
        <div
          className="fixed inset-0 bg-ink/75"
          onClick={step === 'processing' ? undefined : onClose}
          aria-hidden="true"
        />

        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="enrolment-title"
          className="relative flex max-h-[calc(100vh-3rem)] w-full max-w-[860px] flex-col border border-ink/25 bg-paper sm:max-h-[calc(100vh-5rem)]"
        >
          <header className="relative flex-none border-b border-paper/15 bg-pine px-5 py-5 text-paper sm:px-7 sm:py-6">
            <button
              type="button"
              onClick={onClose}
              disabled={step === 'processing'}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center text-paper/60 transition-colors hover:bg-paper/10 hover:text-paper disabled:opacity-30"
              aria-label="Close enrolment"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>

            <div className="flex flex-wrap items-start justify-between gap-5 pr-10">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-paper/65">Enrolment / Clearance</span>
                </div>
                <h2 id="enrolment-title" className="mt-2 max-w-[30ch] text-[clamp(1.3rem,2.6vw,1.8rem)] font-semibold leading-tight tracking-[-0.02em]">
                  {displayTitle || 'Enrol in your programme'}
                </h2>
              </div>
              <div className="min-w-[150px] border-l border-paper/20 pl-5">
                <p className="font-mono text-[8.5px] uppercase tracking-[0.14em] text-paper/65">
                  {discount > 0 ? 'Payable after code' : 'Tuition'}
                </p>
                <p className="tnum mt-1 text-[22px] font-semibold leading-none tracking-[-0.02em]">
                  {discount > 0 ? `₦${payableValue.toLocaleString('en-NG')}` : formattedPrice || '—'}
                </p>
                {discount > 0 && (
                  <p className="tnum mt-1 font-mono text-[9px] text-paper/65 line-through">{formattedPrice}</p>
                )}
              </div>
            </div>

            <CheckpointRail steps={steps} tone="dark" className="mt-5 max-w-[440px]" />
          </header>

          <div className="overflow-y-auto">
            {step === 'details' && (
              <div className="px-5 py-5 sm:px-7 sm:py-6">
                <p className="max-w-[66ch] text-[12.5px] leading-5 text-ink-2">
                  Complete the applicant details below. Payment is processed by Paystack; card, bank transfer and USSD
                  are handled on their secure checkout.
                </p>

                <div className="mt-4 border border-blue-200 bg-blue-50/70 px-4 py-3.5">
                  <div className="flex items-start gap-2.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-blue-700" strokeWidth={1.7} />
                    <div>
                      <p className="text-[13px] font-semibold text-blue-900">
                        {user
                          ? 'Your student account is already linked.'
                          : priceValue === 0
                            ? 'This programme is free.'
                            : 'No student account yet? Pay first.'}
                      </p>
                      <p className="mt-0.5 text-[12px] leading-5 text-blue-800/80">
                        {user
                          ? 'Your enrolment will attach to the account you are signed in with.'
                          : priceValue === 0
                            ? 'Continue to create your free student account; the programme opens in the LMS after your email is confirmed.'
                            : 'After payment we create your LSEM account and email an access link to the address above.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-5 border-b border-rule pb-2">
                  <h3 className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">01 / Applicant</h3>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
                  <Field label="First name" error={errors.firstName}>
                    <input
                      type="text"
                      className="lms-field"
                      value={form.firstName}
                      onChange={(e) => update('firstName', e.target.value)}
                      placeholder="Chinedu"
                      aria-invalid={!!errors.firstName}
                    />
                  </Field>
                  <Field label="Last name" error={errors.lastName}>
                    <input
                      type="text"
                      className="lms-field"
                      value={form.lastName}
                      onChange={(e) => update('lastName', e.target.value)}
                      placeholder="Okafor"
                      aria-invalid={!!errors.lastName}
                    />
                  </Field>
                  <Field label="Email address" error={errors.email}>
                    <input
                      type="email"
                      className="lms-field"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="you@example.com"
                      aria-invalid={!!errors.email}
                    />
                  </Field>
                  <Field label="Phone number" error={errors.phone}>
                    <input
                      type="tel"
                      className="lms-field"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="+234 803 000 0000"
                      aria-invalid={!!errors.phone}
                    />
                  </Field>
                  <Field label="Country" error={errors.country}>
                    <input
                      type="text"
                      className="lms-field"
                      value={form.country}
                      onChange={(e) => update('country', e.target.value)}
                      aria-invalid={!!errors.country}
                    />
                  </Field>
                  <Field label="State / City" error={errors.stateCity}>
                    <input
                      type="text"
                      className="lms-field"
                      value={form.stateCity}
                      onChange={(e) => update('stateCity', e.target.value)}
                      placeholder="Lagos"
                      aria-invalid={!!errors.stateCity}
                    />
                  </Field>
                  <Field label="Occupation" error={errors.occupation}>
                    <input
                      type="text"
                      className="lms-field"
                      value={form.occupation}
                      onChange={(e) => update('occupation', e.target.value)}
                      placeholder="Export Manager"
                      aria-invalid={!!errors.occupation}
                    />
                  </Field>
                  <Field label="Company (optional)">
                    <input
                      type="text"
                      className="lms-field"
                      value={form.company}
                      onChange={(e) => update('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="How did you hear about us?" error={errors.heardAbout}>
                      <select
                        className="lms-field"
                        value={form.heardAbout}
                        onChange={(e) => update('heardAbout', e.target.value)}
                        aria-invalid={!!errors.heardAbout}
                      >
                        <option value="">Select an option</option>
                        {heardOptions.map((o) => (
                          <option key={o} value={o}>{o}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                </div>

                <div className="mt-6 border-b border-rule pb-2">
                  <h3 className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">02 / Payment</h3>
                </div>

                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end">
                  <div className="flex-1">
                    <label htmlFor="promo-code" className="lms-label">Promo code (optional)</label>
                    <input
                      id="promo-code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter a code"
                      className="lms-field font-mono uppercase placeholder:font-sans placeholder:normal-case placeholder:tracking-normal"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void applyPromo()}
                    className="lms-btn-secondary sm:mb-0"
                  >
                    Apply code
                  </button>
                </div>
                {promoMsg && (
                  <p className={`mt-1.5 text-[11.5px] leading-5 ${promo ? 'text-success' : 'text-danger'}`} role="status">{promoMsg}</p>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-y border-rule py-3">
                  <div className="flex items-center gap-2 text-[11.5px] text-ink-2">
                    <span className="h-1.5 w-1.5 bg-success" aria-hidden="true" />
                    Secure checkout through Paystack. Card details never touch LSEM servers.
                  </div>
                  <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">NGN</span>
                </div>

                {errorMsg && (
                  <p className="mt-3 border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger" role="alert">
                    {errorMsg}
                  </p>
                )}

                <div className="mt-5 flex flex-col-reverse gap-2 border-t border-rule pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" onClick={onClose} className="lms-btn-secondary">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void startPayment()}
                    className="lms-btn-stamp"
                  >
                    {priceValue === 0 ? 'Enroll free' : `Pay ${discount > 0 ? `₦${payableValue.toLocaleString('en-NG')}` : (formattedPrice || 'Now')}`}
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            )}

            {step === 'processing' && (
              <div className="flex flex-col items-center px-6 py-14 text-center">
                <Loader2 className="h-9 w-9 animate-spin text-pine" strokeWidth={1.5} />
                <Stamp tone="pending" className="mt-5">{verifying ? 'Checking payment' : 'Awaiting payment'}</Stamp>
                <h3 className="mt-4 text-[19px] font-semibold tracking-[-0.015em] text-ink">
                  {verifying ? 'Confirming your payment…' : 'Opening secure checkout…'}
                </h3>
                <p className="mt-2 max-w-[46ch] text-[12.5px] leading-5 text-ink-2">
                  {verifying
                    ? 'Verifying with Paystack and activating your classroom. Please keep this tab open.'
                    : "Complete your payment in the Paystack window. Don't close this tab."}
                </p>
              </div>
            )}

            {step === 'success' && (
              <div className="px-6 py-8 text-center sm:py-10">
                <span className="mx-auto flex h-12 w-12 items-center justify-center border border-success/30 bg-success-soft">
                  <CheckCircle2 className="h-6 w-6 text-success" strokeWidth={1.5} />
                </span>
                <Stamp tone="cleared" className="mt-4">
                  {priceValue === 0 ? 'Enrolled' : 'Payment received'}
                </Stamp>
                <h3 className="mt-4 text-[22px] font-semibold tracking-[-0.02em] text-ink">
                  {guestCheckout ? 'Check your inbox.' : priceValue === 0 ? 'Your free programme is active.' : 'Your seat is confirmed.'}
                </h3>
                <p className="mx-auto mt-2 max-w-[50ch] text-[13px] leading-6 text-ink-2">
                  Thank you, {form.firstName}. {guestCheckout ? (
                    <>
                      We have your payment for <strong className="font-semibold text-ink">{displayTitle}</strong>.
                      {accessEmailSent
                        ? <> We sent an LMS access link to <strong className="font-semibold text-ink">{form.email}</strong>. Open that email to enter your classroom.</>
                        : <> Access is attached to <strong className="font-semibold text-ink">{form.email}</strong>. Use the Student login link below.</>}
                    </>
                  ) : priceValue === 0 ? (
                    <> Your free enrolment in <strong className="font-semibold text-ink">{displayTitle}</strong> is ready in your LMS dashboard.</>
                  ) : (
                    <> We have your payment for <strong className="font-semibold text-ink">{displayTitle}</strong>. Your classroom access is active.</>
                  )}
                </p>
                {paymentRef && (
                  <div className="mx-auto mt-4 inline-flex items-center gap-2 border border-rule bg-white px-3 py-1.5 font-mono text-[10px] text-ink-2">
                    <span className="uppercase tracking-[0.1em]">Ref</span>
                    <span className="text-ink">{paymentRef}</span>
                  </div>
                )}
                {accessMessage && !verifyMsg && (
                  <p className="mx-auto mt-3 max-w-[52ch] text-[11.5px] leading-5 text-ink-2">{accessMessage}</p>
                )}
                {verifyMsg && (
                  <p className="mx-auto mt-3 max-w-[52ch] border border-warning/25 bg-warning-soft px-3 py-2 text-[11.5px] leading-5 text-warning">
                    {verifyMsg}
                  </p>
                )}
                <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
                  {guestCheckout ? (
                    <a href={lmsHref('/login')} onClick={onClose} className="lms-btn-primary">
                      Open student login
                    </a>
                  ) : bundle ? (
                    <a href={lmsHref('/dashboard')} onClick={onClose} className="lms-btn-primary">
                      Go to dashboard
                    </a>
                  ) : (
                    courseId && (
                      <a href={lmsHref(`/learn/${courseId}`)} onClick={onClose} className="lms-btn-primary">
                        Go to classroom
                      </a>
                    )
                  )}
                  <button onClick={onClose} className="lms-btn-secondary">
                    Done
                  </button>
                </div>
              </div>
            )}

            {step === 'error' && (
              <div className="px-6 py-8 text-center sm:py-10">
                <span className="mx-auto flex h-12 w-12 items-center justify-center border border-danger/30 bg-danger-soft">
                  <AlertCircle className="h-6 w-6 text-danger" strokeWidth={1.5} />
                </span>
                <Stamp tone="held" className="mt-4">Not started</Stamp>
                <h3 className="mt-4 text-[22px] font-semibold tracking-[-0.02em] text-ink">
                  Payment could not start
                </h3>
                <p className="mx-auto mt-2 max-w-[50ch] text-[13px] leading-6 text-ink-2">{errorMsg}</p>
                <div className="mt-6 flex justify-center gap-2">
                  <button onClick={() => setStep('details')} className="lms-btn-secondary">
                    Back to details
                  </button>
                  <button onClick={onClose} className="lms-btn-primary">
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface FieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, error, children }) => (
  <label className="block">
    <span className="lms-label">{label}</span>
    {children}
    {error && <span className="mt-1 block text-[11px] text-danger">{error}</span>}
  </label>
);

export default EnrollmentModal;
